import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const OPT_OUT_KEY = 'zp-shipping-protection-opt-out';
const PROTECTION_HANDLE = 'shipping-protection';
const SOURCE = 'shipping-protection-component';

// Module-level state: survives section morphs that re-create the element, and
// keeps a queued reconcile alive even if the component is briefly disconnected.
let queue = Promise.resolve();
let runQueued = false;
let lastCartHadProtection = null;

function getOptOut() {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function setOptOut(value) {
  try {
    if (value) {
      localStorage.setItem(OPT_OUT_KEY, '1');
    } else {
      localStorage.removeItem(OPT_OUT_KEY);
    }
  } catch {
    // Private browsing — degrade to session-only behaviour.
  }
}

/**
 * @param {{ tiers: Array<{id: number, minUsd: number, maxUsd: number, price: string}>, element?: Element }} config
 */
function enqueueReconcile(config) {
  if (runQueued) return;
  runQueued = true;
  queue = queue
    .then(() => {
      runQueued = false;
      return reconcile(config);
    })
    .catch((error) => {
      console.warn('[shipping-protection] reconcile failed:', error);
    });
}

/**
 * Brings the cart's protection line in sync with the physical subtotal:
 * exactly one unit of the matching tier when protection is active, none when
 * the customer opted out or the cart has no physical items.
 *
 * @param {{ tiers: Array<{id: number, minUsd: number, maxUsd: number, price: string}>, element?: Element }} config
 */
async function reconcile(config) {
  const { tiers } = config;
  if (!tiers.length) return;

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  const cart = await response.json();

  const tierIds = new Set(tiers.map((tier) => tier.id));
  const protectionLines = cart.items.filter(
    (item) => item.handle === PROTECTION_HANDLE || tierIds.has(item.variant_id)
  );
  const physicalLines = cart.items.filter((item) => !protectionLines.includes(item));
  const physicalQuantity = physicalLines.reduce((sum, item) => sum + item.quantity, 0);
  const physicalSubtotal = physicalLines.reduce((sum, item) => sum + item.final_line_price, 0);

  // Protection vanished while physical items remain and we did not remove it
  // ourselves: the customer deleted the line (cart row X, checkout page…).
  // Treat that as an explicit opt-out so we do not fight the customer.
  if (lastCartHadProtection === true && protectionLines.length === 0 && physicalQuantity > 0) {
    setOptOut(true);
  }

  let targetVariantId = null;
  if (physicalQuantity > 0 && !getOptOut()) {
    targetVariantId = findTier(tiers, physicalSubtotal)?.id ?? null;
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of protectionLines) {
    const wanted = line.variant_id === targetVariantId ? 1 : 0;
    if (line.quantity !== wanted) updates[line.variant_id] = wanted;
  }
  if (targetVariantId && !protectionLines.some((line) => line.variant_id === targetVariantId)) {
    updates[targetVariantId] = 1;
  }

  if (Object.keys(updates).length === 0) {
    lastCartHadProtection = protectionLines.length > 0;
    return;
  }

  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sectionIds.add(element.dataset.sectionId);
    }
  });

  const body = JSON.stringify({
    updates,
    sections: Array.from(sectionIds).join(','),
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  const dispatcher = config.element?.isConnected ? config.element : document.documentElement;
  dispatcher.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: Object.entries(updates).map(([merchandiseId, quantity]) => ({ merchandiseId, quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const updateResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const result = await updateResponse.json();

    if (result.status || result.errors) {
      throw new Error(result.description || result.message || 'Cart update failed');
    }

    lastCartHadProtection = Boolean(result.items?.some((item) => tierIds.has(item.variant_id)));

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
      detail: {
        sections: result.sections,
        items: result.items,
        itemCount: result.item_count,
        source: SOURCE,
        didError: false,
      },
    });

    // Morph the returned sections ourselves: cart-items-component does the same
    // on our event, but its script loads lazily and may not be listening yet
    // (e.g. during the page-load auto-add). Morphing twice is a cheap no-op.
    for (const [sectionId, html] of Object.entries(result.sections ?? {})) {
      if (!html) continue;
      const host = [...document.querySelectorAll('cart-items-component')].find(
        (element) => element instanceof HTMLElement && element.dataset.sectionId === sectionId
      );
      const mode = host && host.dataset.drawer !== undefined ? 'hydration' : 'full';
      morphSection(sectionId, html, { mode });
    }
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * @param {Array<{id: number, minUsd: number, maxUsd: number, price: string}>} tiers
 * @param {number} presentmentCents - Physical subtotal in presentment currency cents.
 */
function findTier(tiers, presentmentCents) {
  const rate = parseFloat(window.Shopify?.currency?.rate ?? '1') || 1;
  const usdCents = Math.round(presentmentCents / rate);

  return (
    tiers.find(
      (tier) => usdCents >= tier.minUsd * 100 && (tier.maxUsd < 0 || usdCents < tier.maxUsd * 100)
    ) ?? tiers[tiers.length - 1]
  );
}

function whenCartComponentsReady() {
  const definitions = Promise.all([
    customElements.whenDefined('cart-items-component'),
    customElements.whenDefined('cart-icon'),
  ]);
  if (document.readyState === 'complete') return Promise.resolve();
  const loaded = new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  return Promise.race([definitions, loaded]);
}

/**
 * Cart shipping-protection toggle.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [checkbox] - The toggle input.
 * @property {HTMLElement} [price] - The tier price display.
 *
 * @extends {Component<Refs>}
 */
class ShippingProtectionComponent extends Component {
  // Morphs patch attributes in place without re-connecting the element, so the
  // pending-price fill must react to attribute changes, not just connection.
  static observedAttributes = ['data-needs-price', 'data-physical-subtotal'];

  attributeChangedCallback() {
    this.#fillPendingPrice();
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#fillPendingPrice();

    // Defer the page-load reconcile until the cart components are defined, so
    // they observe our cart update too. The load event caps the wait on pages
    // where one of them never registers.
    whenCartComponentsReady().then(() => enqueueReconcile(this.#config));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  get #config() {
    let tiers = [];
    try {
      tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch {
      tiers = [];
    }
    tiers.sort((a, b) => a.minUsd - b.minUsd);
    return { tiers, element: this };
  }

  /** Declarative handler for the toggle (`on:change="/onToggle"`). */
  onToggle() {
    const { checkbox } = this.refs;
    if (!(checkbox instanceof HTMLInputElement)) return;

    setOptOut(!checkbox.checked);
    this.classList.toggle('shipping-protection--off', !checkbox.checked);
    enqueueReconcile(this.#config);
  }

  /**
   * Reconciles after cart changes made by other components (quantity edits,
   * line removals, discount updates that re-render the section…).
   *
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    if (event.target === this) return;

    const promise = event.promise ?? Promise.resolve(null);
    promise
      .then((result) => {
        if (result?.detail?.source === SOURCE) return;
        enqueueReconcile(this.#config);
      })
      .catch(() => {
        // The owning component reports its own errors; nothing to reconcile.
      });
  };

  /**
   * Fills the tier price when Liquid could not compute it server-side
   * (non-USD presentment currency).
   */
  #fillPendingPrice() {
    if (!this.hasAttribute('data-needs-price')) return;
    const { price } = this.refs;
    if (!(price instanceof HTMLElement)) return;

    const subtotal = parseInt(this.dataset.physicalSubtotal ?? '0', 10);
    const tier = findTier(this.#config.tiers, subtotal);
    if (tier) {
      price.textContent = tier.price;
      this.removeAttribute('data-needs-price');
    }
  }
}

if (!customElements.get('shipping-protection-component')) {
  customElements.define('shipping-protection-component', ShippingProtectionComponent);
}
